import { MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router";

import { useAuth } from "@/app/providers/AuthProvider";
import { isEntryActive, isRouteActive, MOBILE_MORE_NAV, MOBILE_NAV } from "@/components/layout/navigation";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import LogoutDialog from "@/features/auth/components/LogoutDialog";
import { useCurrentOrganization } from "@/features/organization/hooks/useOrganization";
import { cn } from "@/lib/utils";

/**
 * Phones get a bottom bar rather than a drawer: the tabs stay under the thumb
 * and the current section is always visible.
 */
export default function BottomNav() {
  const location = useLocation();
  const { logout } = useAuth();
  const { organization } = useCurrentOrganization();
  const [moreOpen, setMoreOpen] = useState(false);
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  const handleLogout = async () => {
    setConfirmingLogout(false);
    setMoreOpen(false);
    await logout();
  };

  return (
    <>
      {confirmingLogout && <LogoutDialog open onLogout={handleLogout} onCancel={() => setConfirmingLogout(false)} />}

      <nav
        aria-label="Navigation principale"
        className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        <ul className="grid" style={{ gridTemplateColumns: `repeat(${MOBILE_NAV.length + 1}, minmax(0, 1fr))` }}>
          {MOBILE_NAV.map((entry) => {
            const active = isEntryActive(location.pathname, entry);
            return (
              <li key={entry.to}>
                <Link
                  to={entry.to}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex h-16 flex-col items-center justify-center gap-1 px-1 transition",
                    active ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn("h-1 w-6 rounded-full transition-colors", active ? "bg-brand" : "bg-transparent")}
                  />
                  {entry.icon}
                  <span className="text-[11px] font-medium leading-none text-center">{shortLabel(entry.label)}</span>
                </Link>
              </li>
            );
          })}

          <li>
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-expanded={moreOpen}
              aria-haspopup="dialog"
              className={cn(
                "flex h-16 w-full flex-col items-center justify-center gap-1 transition",
                MOBILE_MORE_NAV.some((entry) => isEntryActive(location.pathname, entry))
                  ? "text-foreground"
                  : "text-muted-foreground",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "h-1 w-6 rounded-full transition-colors",
                  MOBILE_MORE_NAV.some((entry) => isEntryActive(location.pathname, entry))
                    ? "bg-brand"
                    : "bg-transparent",
                )}
              />
              <MoreHorizontal size={20} />
              <span className="text-[11px] font-medium leading-none">Plus</span>
            </button>
          </li>
        </ul>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader>
            <SheetTitle>Catalogue et production</SheetTitle>
            <p className="text-sm text-muted-foreground">{organization?.name ?? "Nafa Pro"}</p>
          </SheetHeader>

          <div className="flex flex-col gap-1 px-4 pb-8">
            {MOBILE_MORE_NAV.map((entry) => (
              <Link
                key={entry.to}
                to={entry.to}
                onClick={() => setMoreOpen(false)}
                aria-current={isRouteActive(location.pathname, entry.to) ? "page" : undefined}
                className={cn(
                  "flex h-12 items-center gap-3 rounded-lg px-4 text-[15px] transition",
                  isRouteActive(location.pathname, entry.to)
                    ? "bg-foreground font-semibold text-background"
                    : "hover:bg-muted",
                )}
              >
                {entry.icon}
                {entry.label}
              </Link>
            ))}

            <button
              type="button"
              onClick={() => setConfirmingLogout(true)}
              className="h-12 rounded-lg px-4 text-left text-[15px] text-muted-foreground transition hover:bg-muted"
            >
              Se déconnecter
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

/** Keep the labels short enough for a compact bottom bar. */
const SHORT_LABELS: Record<string, string> = {
  "Tableau de bord": "Accueil",
};

function shortLabel(label: string): string {
  return SHORT_LABELS[label] ?? label;
}
